/**
 * Vesper useProfile Hook (Shared)
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/userSlice';
import {
  getMe,
  updateMe,
  getMyScore,
  getMyEventHistory,
  updatePrivacy,
  startVerification,
} from '../services/user.service';
import type {
  UserPublicProfile,
  UserTrustScore,
  UserEventHistoryItem,
  PrivacySettings,
  UpdateProfilePayload,
  UpdatePrivacyPayload,
} from '../types/user.types';

export interface UseProfileReturn {
  profile: UserPublicProfile | null;
  score: UserTrustScore | null;
  eventHistory: UserEventHistoryItem[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  saveError: string | null;
  saveProfile: (fields: UpdateProfilePayload) => Promise<void>;
  savePrivacy: (payload: UpdatePrivacyPayload) => Promise<PrivacySettings | null>;
  initiateVerification: (type: 'face' | 'id') => Promise<string | null>;
  refresh: () => Promise<void>;
}

export function useProfile(): UseProfileReturn {
  const updateStoreUser = useAuthStore((s) => s.updateUser);

  const [profile, setProfile]           = useState<UserPublicProfile | null>(null);
  const [score, setScore]               = useState<UserTrustScore | null>(null);
  const [eventHistory, setEventHistory] = useState<UserEventHistoryItem[]>([]);
  const [isLoading, setIsLoading]       = useState(true);
  const [isSaving, setIsSaving]         = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [saveError, setSaveError]       = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [p, s, h] = await Promise.all([
        getMe(),
        getMyScore(),
        getMyEventHistory(),
      ]);
      setProfile(p);
      setScore(s);
      setEventHistory(h);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const saveProfile = useCallback(async (fields: UpdateProfilePayload): Promise<void> => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await updateMe(fields);
      setProfile(updated);
      updateStoreUser({ displayName: updated.displayName, avatarUrl: updated.avatarUrl });
    } catch (err) {
      // Set saveError for UI components that render the inline error banner,
      // AND re-throw so awaiting callers can branch on success/failure.
      // Without the re-throw, a failed save would resolve cleanly and
      // callers would fire success toasts / close panels with stale data
      // — exactly the "stuck on Climate" symptom this fix addresses.
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [updateStoreUser]);

  const savePrivacy = useCallback(
    async (payload: UpdatePrivacyPayload): Promise<PrivacySettings | null> => {
      setIsSaving(true);
      setSaveError(null);
      try {
        const settings = await updatePrivacy(payload);
        if (profile && payload.scoreVisible !== undefined) {
          setProfile({ ...profile, trustScoreVisible: payload.scoreVisible });
        }
        return settings;
      } catch (err) {
        // Same pattern as saveProfile — set state for inline UI AND re-throw
        // so awaiting callers know the persist failed.
        setSaveError(err instanceof Error ? err.message : 'Privacy update failed');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [profile],
  );

  const initiateVerification = useCallback(
    async (type: 'face' | 'id'): Promise<string | null> => {
      const result = await startVerification(type);
      if (result?.verificationUrl) {
        if (profile) {
          setProfile({ ...profile, verificationStatus: 'pending' });
        }
        return result.verificationUrl;
      }
      return null;
    },
    [profile],
  );

  return {
    profile,
    score,
    eventHistory,
    isLoading,
    isSaving,
    error,
    saveError,
    saveProfile,
    savePrivacy,
    initiateVerification,
    refresh: load,
  };
}
